
import Vue from 'vue';
import { Location } from 'vue-router';

import { LocationTree, TreeBadge } from '@/interfaces/locationTree';
import { Alert } from '@/interfaces/alerts';
import { Room, Zone } from '@/interfaces/lighting';
import { Floor } from '@/interfaces/locations';
import { ExpansionCache } from '@/lib/treeExpansionCache';
import TreeView from '@/components/dashboard/TreeView.vue';
import permissions from '@/lib/permissions';

export default Vue.extend({
	components: {
		TreeView,
	},
	computed: {
		userCanReadCalibrations(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'groups',
				'read',
			]);
		},
		expansionCache(): ExpansionCache {
			return new ExpansionCache(this.$route.query.expanded);
		},
		treeData(): LocationTree[] {
			if (this.$store.getters['locations/floors/getFloors'].length !== 0) {
				return this.$store.getters['locations/floors/getSiteFloors'](
					this.$store.getters['tenancy/sites/getSelectedSite'].id
				)
					.sort((floorA: Floor, floorB: Floor) => {
						const nameA = floorA.name.toUpperCase();
						const nameB = floorB.name.toUpperCase();
						if (nameA < nameB) {
							return -1;
						}
						if (nameA > nameB) {
							return 1;
						}
						return 0;
					})
					.map((floor: Floor) => {
						const nodes: LocationTree[] = this.$store.getters['locations/rooms/getFloorRooms'](
							floor.id
						)
							.sort((roomA: Room, roomB: Room) => {
								const nameA = roomA.name.toUpperCase();
								const nameB = roomB.name.toUpperCase();
								if (nameA < nameB) {
									return -1;
								}
								if (nameA > nameB) {
									return 1;
								}
								return 0;
							})
							.map(this.getFloorChild);
						const alertBadges: TreeBadge[] = [];

						const acknowledgedAlerts = nodes.reduce((acknowledgedAlertsTotal, node) => {
							return (acknowledgedAlertsTotal += node.acknowledgedAlerts ?? 0);
						}, 0);

						const newAlerts = nodes.reduce((newAlertsTotal, node) => {
							return (newAlertsTotal += node.newAlerts ?? 0);
						}, 0);

						if (newAlerts > 0) {
							alertBadges.push(this.alertBadgeCreator('new', newAlerts));
						}

						if (acknowledgedAlerts > 0) {
							alertBadges.push(this.alertBadgeCreator('known', acknowledgedAlerts));
						}

						if (alertBadges.length === 0) {
							alertBadges.push(this.alertBadgeCreator('ok'));
						}

						return {
							...floor,
							expanded: this.expansionCache.contains({ category: 'Floor', id: floor.id }),
							category: 'Floor',
							acknowledgedAlerts: acknowledgedAlerts,
							newAlerts: newAlerts,
							badges: alertBadges,
							nodes: nodes,
						};
					});
			} else {
				return [];
			}
		},
	},

	methods: {
		onTreeExpanded(opened: boolean, category: string, id: number) {
			if (opened) {
				this.expansionCache.add({ category, id });
			} else {
				this.expansionCache.remove({ category, id });
			}
			this.$router.replace({
				...this.$route,
				query: { expanded: this.expansionCache.queryParams },
			} as Location);
		},
		navigation(id: string) {
			this.$router.push({ name: 'Zone', params: { zoneId: id } });
		},

		getFloorChild(room: Room): LocationTree {
			const nodes: LocationTree[] = this.$store.getters['locations/zones/getRoomZones'](room.id)
				.sort((zoneA: Zone, zoneB: Zone) => {
					const nameA = zoneA.name.toUpperCase();
					const nameB = zoneB.name.toUpperCase();
					if (nameA < nameB) {
						return -1;
					}
					if (nameA > nameB) {
						return 1;
					}
					return 0;
				})
				.map(this.getRoomChild);
			const alertBadges: TreeBadge[] = [];

			const acknowledgedAlerts: number = nodes.reduce((acknowledgedAlertsTotal, node) => {
				return (acknowledgedAlertsTotal += node.acknowledgedAlerts ?? 0);
			}, 0);

			const newAlerts: number = nodes.reduce((newAlertsTotal, node) => {
				return (newAlertsTotal += node.newAlerts ?? 0);
			}, 0);

			if (newAlerts > 0) {
				alertBadges.push(this.alertBadgeCreator('new', newAlerts));
			}

			if (acknowledgedAlerts > 0) {
				alertBadges.push(this.alertBadgeCreator('known', acknowledgedAlerts));
			}

			if (alertBadges.length === 0) {
				alertBadges.push(this.alertBadgeCreator('ok'));
			}

			return {
				...room,
				expanded: this.expansionCache.contains({ category: 'Room', id: room.id }),
				category: 'Room',
				acknowledgedAlerts: acknowledgedAlerts,
				newAlerts: newAlerts,
				badges: alertBadges,
				nodes: nodes,
			};
		},

		getRoomChild(zone: Zone): LocationTree {
			let acknowledgedAlerts = 0;
			let newAlerts = 0;
			const zoneAlerts: Alert[][] = zone.devices!.map((device: number) =>
				this.$store.getters['activities/alerts/getDeviceActiveAlerts'](device)
			);
			const alertBadges: TreeBadge[] = [];

			if (zoneAlerts.length > 0) {
				acknowledgedAlerts = this.filterAlerts(zoneAlerts, 'known');
				newAlerts = this.filterAlerts(zoneAlerts, 'new');

				if (newAlerts > 0) {
					alertBadges.push(this.alertBadgeCreator('new', newAlerts));
				}

				if (acknowledgedAlerts > 0) {
					alertBadges.push(this.alertBadgeCreator('known', acknowledgedAlerts));
				}
			}

			if (alertBadges.length === 0) {
				alertBadges.push(this.alertBadgeCreator('ok'));
			}

			return {
				...zone,
				expanded: false,
				category: 'Zone',
				changeNavigation: String(zone.id),
				acknowledgedAlerts: acknowledgedAlerts,
				newAlerts: newAlerts,
				badges: alertBadges,
			};
		},

		alertBadgeCreator(type: string, alertTotal?: number): TreeBadge {
			switch (type) {
				case 'new':
					return {
						badgeName: this.$t('alerts.new_alerts', { count: alertTotal }).toString(),
						badgeColor: 'danger',
						category: 'newAlerts',
					};
				case 'known':
					return {
						badgeName: this.$t('alerts.acknowledged_alerts', { count: alertTotal }).toString(),
						badgeColor: 'warning',
						category: 'acknowledgedAlerts',
					};
				case 'ok':
					return {
						badgeName: this.$t('OK').toString(),
						badgeColor: 'success-alt',
						category: 'okAlerts',
					};
				default:
					return {
						badgeName: 'errorBadge',
						badgeColor: 'warning',
						category: 'errorBadge',
					};
			}
		},

		filterAlerts(alerts: Alert[][], type: string): number {
			let alertFilter: (a: Alert) => boolean;
			switch (type) {
				case 'new':
					alertFilter = (a) => !a.ack;
					break;
				case 'known':
					alertFilter = (a) => a.ack;
					break;
				default:
					alertFilter = () => true;
					break;
			}
			return alerts
				.map((alerts) => alerts.filter(alertFilter).length)
				.reduce((total, count) => total + count, 0);
		},
	},
});
